// ─── Settings → Streaming Method ─────────────────────────────────────────────
// Card-style settings panel mirroring the XD reference. A Device dropdown
// scopes the setting per-device (rows keyed by project+device on the
// server); the Option dropdown then drives which input group renders
// below it. Each method has its own field set:
//
//   WQData LIVE       — no extra fields
//   FTP               — Host · Port · User · Password · Directory · Transfer Mode
//   Iridium Satellite — IMEI · Custom Transmit Email · Custom Command Email
//
// Backed by /api/data/streaming-method/ — server stores all fields on the
// same row and zeroes out non-matching fields whenever `method` changes,
// so a row never carries stale config from a previously-selected method.

import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, Loader, AlertCircle } from 'lucide-react'
import { getStreamingMethod, updateStreamingMethod } from '../../../lib/settingsApi'
import { projectsApi } from '../../../lib/projectsApi'

// "Email" was removed from the Streaming Method picker — devices either
// post to WQData LIVE, push to an FTP target, or transmit over Iridium.
// Legacy rows in the DB that still carry method='Email' will fall back to
// 'WQData LIVE' on display (see the load() effect below).
const OPTIONS = ['WQData LIVE', 'FTP', 'Iridium Satellite']

// Empty payload used when initialising or resetting the form. Keys match
// the API serializer 1:1 so we can spread directly into the PATCH body.
const BLANK = {
    method:                 'WQData LIVE',
    email:                  '',
    ftp_host:               '',
    ftp_port:               '',
    ftp_user:               '',
    ftp_password:           '',
    ftp_directory:          '',
    ftp_transfer_mode:      '',
    iridium_imei:           '',
    iridium_transmit_email: '',
    custom_command_email:   '',
}

export default function StreamingMethodPanel({ projectId, onCancel }) {
    const [devices,  setDevices]  = useState([])   // [{id, name}]
    const [deviceId, setDeviceId] = useState(null) // selected device (null = project-wide)
    const [form,     setForm]     = useState(BLANK)
    const [original, setOriginal] = useState(BLANK)
    const [loading,  setLoading]  = useState(false)
    const [saving,   setSaving]   = useState(false)
    const [error,    setError]    = useState(null)
    const [saved,    setSaved]    = useState(false)

    // Flatten the project → sites → devices tree into a single list for
    // the dropdown. Names are prefixed with the site so devices from
    // different sites stay distinguishable even when a name is reused.
    const deviceOptions = useMemo(
        () => devices.map((d) => ({
            id:    d.id,
            label: d.siteName ? `${d.siteName} — ${d.name}` : d.name,
        })),
        [devices],
    )

    // Load devices once per project. When the list arrives, auto-select
    // the first device so the panel isn't empty on first open.
    useEffect(() => {
        if (!projectId) { setDevices([]); setDeviceId(null); return }
        let cancelled = false
        projectsApi.fetchProjectSites(projectId)
            .then((sites) => {
                if (cancelled) return
                const flat = []
                for (const s of sites || []) {
                    for (const d of (s.devices || [])) {
                        flat.push({ id: d.id, name: d.name || `Device ${d.id}`, siteName: s.name || '' })
                    }
                }
                setDevices(flat)
                setDeviceId((prev) => prev || flat[0]?.id || null)
            })
            .catch(() => { if (!cancelled) setDevices([]) })
        return () => { cancelled = true }
    }, [projectId])

    const load = async () => {
        if (!projectId) return
        setLoading(true); setError(null); setSaved(false)
        try {
            const r = await getStreamingMethod(projectId, deviceId)
            // Snap any unsupported stored method (e.g. legacy 'Email') back
            // to 'WQData LIVE' so the dropdown shows a valid selection.
            const rawMethod = r?.method || 'WQData LIVE'
            const safeMethod = OPTIONS.includes(rawMethod) ? rawMethod : 'WQData LIVE'
            const next = { ...BLANK, ...(r || {}), method: safeMethod, ftp_port: r?.ftp_port ?? '' }
            setForm(next); setOriginal(next)
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || 'Failed to load streaming method')
        } finally {
            setLoading(false)
        }
    }
    // Reload whenever the project OR selected device changes.
    useEffect(() => { load() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [projectId, deviceId])

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }))

    const onUpdate = async () => {
        setSaving(true); setError(null); setSaved(false)
        try {
            // Only send the fields relevant to the selected method (the
            // server clears the rest, but this keeps the payload tidy).
            const body = { method: form.method }
            if (form.method === 'Email')             body.email = form.email
            if (form.method === 'FTP') {
                body.ftp_host          = form.ftp_host
                body.ftp_port          = form.ftp_port
                body.ftp_user          = form.ftp_user
                body.ftp_password      = form.ftp_password
                body.ftp_directory     = form.ftp_directory
                body.ftp_transfer_mode = form.ftp_transfer_mode
            }
            if (form.method === 'Iridium Satellite') {
                body.iridium_imei           = form.iridium_imei
                body.iridium_transmit_email = form.iridium_transmit_email
                body.custom_command_email   = form.custom_command_email
            }
            const r = await updateStreamingMethod({ projectId, deviceId, ...body })
            const next = { ...BLANK, ...(r || {}), ftp_port: r?.ftp_port ?? '' }
            setForm(next); setOriginal(next)
            setSaved(true)
            setTimeout(() => setSaved(false), 2500)
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || 'Failed to update streaming method')
        } finally {
            setSaving(false)
        }
    }
    // Discard any unsaved edits, then leave the panel (the parent decides where
    // to go — e.g. back to the Dashboard). Previously this only reset the form,
    // so when there was nothing to revert the button appeared to do nothing.
    const handleCancel = () => {
        setForm(original); setError(null); setSaved(false)
        onCancel?.()
    }

    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-6 w-full max-w-xl h-fit self-start mx-auto">
            <h2 className="common-head25 text-lg font-bold text-[#4D4D4D] mb-4 pb-3 border-b border-gray-200">
                Streaming Method
            </h2>

            {error && (
                <div className="common-size15 mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" /> {error}
                </div>
            )}
            {saved && (
                <div className="common-size15 font-semibold mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                    Saved.
                </div>
            )}

            {/* Device dropdown — scopes the setting per-device. Rendered
                first so the user picks a device before configuring how
                its data streams. */}
            <Field label="Device">
                {deviceOptions.length === 0 ? (
                    <div className="common-size15 w-full border border-gray-200 rounded px-3 py-2 text-sm text-gray-500 bg-gray-50">
                        No devices in this project yet.
                    </div>
                ) : (
                    <div className="relative">
                        <select
                            value={deviceId ?? ''}
                            onChange={(e) => setDeviceId(e.target.value ? Number(e.target.value) : null)}
                            disabled={loading || saving}
                            className="common-size15 w-full appearance-none border border-gray-300 rounded px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:border-[#358ECD] disabled:opacity-50"
                        >
                            {deviceOptions.map((d) => (
                                <option key={d.id} value={d.id}>{d.label}</option>
                            ))}
                        </select>
                        <ChevronDown className="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                )}
            </Field>

            <Field label="Option">
                <SelectInput
                    value={form.method}
                    onChange={(v) => setField('method', v)}
                    disabled={loading || saving}
                    options={OPTIONS}
                />
            </Field>

            {/* Per-option field groups. Render order matches the screenshots
                so the panel reads naturally without an explicit divider. */}
            {form.method === 'Email' && (
                <Field label="Email">
                    <TextInput type="email" value={form.email} onChange={(v) => setField('email', v)} disabled={loading || saving} />
                </Field>
            )}

            {form.method === 'FTP' && (
                <>
                    <Field label="Host"><TextInput value={form.ftp_host}          onChange={(v) => setField('ftp_host',          v)} disabled={loading || saving} /></Field>
                    <Field label="Port"><TextInput value={form.ftp_port ?? ''}    onChange={(v) => setField('ftp_port',          v)} disabled={loading || saving} type="number" /></Field>
                    <Field label="User"><TextInput value={form.ftp_user}          onChange={(v) => setField('ftp_user',          v)} disabled={loading || saving} /></Field>
                    <Field label="Password"><TextInput type="password" value={form.ftp_password} onChange={(v) => setField('ftp_password', v)} disabled={loading || saving} /></Field>
                    <Field label="Directory"><TextInput value={form.ftp_directory}     onChange={(v) => setField('ftp_directory',     v)} disabled={loading || saving} /></Field>
                    <Field label="Transfer Mode"><TextInput value={form.ftp_transfer_mode} onChange={(v) => setField('ftp_transfer_mode', v)} disabled={loading || saving} placeholder="ASCII / Binary" /></Field>
                </>
            )}

            {form.method === 'Iridium Satellite' && (
                <>
                    <Field label="IMEI"><TextInput value={form.iridium_imei}           onChange={(v) => setField('iridium_imei',           v)} disabled={loading || saving} /></Field>
                    <Field label="Custom Transmit Email"><TextInput type="email" value={form.iridium_transmit_email} onChange={(v) => setField('iridium_transmit_email', v)} disabled={loading || saving} /></Field>
                    <Field label="Custom Command Email"><TextInput  type="email" value={form.custom_command_email}   onChange={(v) => setField('custom_command_email',   v)} disabled={loading || saving} /></Field>
                </>
            )}

            <button
                type="button"
                onClick={onUpdate}
                disabled={loading || saving}
                className="common-size15 w-full mt-6 py-2.5 bg-[#358ECD] hover:bg-[#2A78B0] text-white text-sm font-bold tracking-wider rounded disabled:opacity-50 inline-flex items-center justify-center gap-1"
            >
                {saving && <Loader className="w-3.5 h-3.5 animate-spin" />}
                UPDATE
            </button>
            <button
                type="button"
                onClick={handleCancel}
                disabled={loading || saving}
                className="common-size15 block mx-auto mt-2 text-xs text-[#358ECD] hover:underline disabled:opacity-50"
            >
                Cancel
            </button>
        </div>
    )
}

function Field({ label, children }) {
    return (
        <label className="block mb-4">
            <span className="common-size15 block font-semibold text-sm text-[#4D4D4D] mb-1.5">{label}</span>
            {children}
        </label>
    )
}
function SelectInput({ value, onChange, options, disabled }) {
    return (
        <div className="relative">
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className="w-full appearance-none border border-gray-300 rounded px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:border-[#358ECD] disabled:opacity-50"
            >
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
    )
}
function TextInput({ value, onChange, disabled, type = 'text', placeholder = '' }) {
    return (
        <input
            type={type}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            placeholder={placeholder}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD] disabled:opacity-50"
        />
    )
}
